/**
 * history.js — closed lists: day-by-day rows, headline numbers and the CSV export.
 */

import { BUILTIN_LISTS, STATUS, STATUS_META } from '../config/lists';
import { itemStatus, isHandled, isFlagged, isUrgent } from './items';
import { listDayOf } from './openList';

// Built-in list name for the CSV when the caller gives no namer (custom lists: their id).
const builtinLabel = id => {
  const l = BUILTIN_LISTS.find(x => x.id === id);
  return l ? (l.short || l.label) : id;
};

/**
 * Item → cells parallel to CSV_HEADERS (List for, Noted, List, Item, Qty, Status, Urgent,
 * Added by, Handled by, Closed on, Closed by). `listLabel(id)` names the list (custom
 * lists included); defaults to the built-in short name.
 */
export function itemToCsvRow(it, listLabel) {
  const st = itemStatus(it);
  return [
    listDayOf(it), it.date, (listLabel || builtinLabel)(it.category), it.name, it.qty || '',
    st === STATUS.NEEDED ? 'Not done' : ((STATUS_META[st] || {}).label || st), isUrgent(it) ? 'yes' : '',
    it.addedBy || '', it.doneBy || it.flaggedBy || '', it.archivedOn || '', it.archivedBy || '',
  ];
}

export const itemsToCsvRows = (items, listLabel) => (items || []).map(it => itemToCsvRow(it, listLabel));

/** Most frequent non-empty value (who closed a list, etc.). */
function mostCommon(values) {
  const counts = new Map();
  values.forEach(v => { if (v) counts.set(v, (counts.get(v) || 0) + 1); });
  let best = ''; let n = 0;
  counts.forEach((c, v) => { if (c > n) { best = v; n = c; } });
  return best;
}

/**
 * One row per closed list-day — the day-by-day tracking view: how many items, how
 * many got done / weren't / were flagged, completion %, and who/when it was closed.
 * Sorted newest first.
 */
export function historyByDay(items) {
  const byDay = new Map();
  for (const it of items || []) {
    const day = listDayOf(it);
    if (!day) continue;
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(it);
  }
  return [...byDay.entries()].map(([day, list]) => {
    const done = list.filter(isHandled).length;
    const flagged = list.filter(isFlagged).length;
    const notDone = list.length - done - flagged;
    const closedOn = list.map(i => i.archivedOn || '').filter(Boolean).sort().pop() || '';
    return {
      key: day, day, items: list, total: list.length, done, flagged, notDone,
      pct: list.length ? Math.round((done / list.length) * 100) : 0,
      closedOn, closedBy: mostCommon(list.map(i => i.archivedBy)),
      addedBy: [...new Set(list.map(i => i.addedBy).filter(Boolean))],
    };
  }).sort((a, b) => b.day.localeCompare(a.day));
}

/** Headline numbers for a history window. */
export function historyTotals(items) {
  const list = items || [];
  const done = list.filter(isHandled).length;
  const flagged = list.filter(isFlagged).length;
  return {
    days: new Set(list.map(listDayOf).filter(Boolean)).size,
    items: list.length, done, flagged,
    notDone: list.length - done - flagged,
    pct: list.length ? Math.round((done / list.length) * 100) : 0,
  };
}
